import { useAuthInfo, useOrgHelper } from "@propelauth/react";
import { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { cn } from "../../../lib/utils";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import SettingsSidebar, { type SettingsSection } from "./SettingsSidebar";

type BreadcrumbItem = {
  label: string;
  to?: string;
};

const INTEGRATION_LABELS: Record<string, string> = {
  slack: "Slack",
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  gmail: "Gmail",
  "google-calendar": "Google Calendar",
  "meeting-note-taker": "Meeting Note Taker",
  "sdk-setup": "Hyperengage SDK",
  "segment-setup": "Segment",
};

const toTitleCase = (value: string) => value
  .split("-")
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

const SettingsLayout = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgHelper } = useOrgHelper();
  const { accessHelper } = useAuthInfo();
  const workspace = useWorkspaceStore((state) => state.workspace);

  const userRole = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return undefined;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.userAssignedRole as string | undefined;
  }, [orgHelper, workspace?.unique_identifier]);

  useEffect(() => {
    if (!workspaceId) return;
    const basePath = `/home/${workspaceId}/settings`;
    if (location.pathname === basePath || location.pathname === `${basePath}/`) {
      navigate(`${basePath}/workspace`, { replace: true });
      return;
    }
    if (
      userRole
      && userRole !== "Owner"
      && userRole !== "Admin"
      && location.pathname.includes("/settings/security")
    ) {
      navigate(`${basePath}/workspace`, { replace: true });
    }
    if (
      location.pathname.includes("/settings/api-keys")
      && !accessHelper?.hasPermission(workspace?.id, "can_read_api_keys")
    ) {
      navigate(`${basePath}/workspace`, { replace: true });
    }
  }, [location.pathname, navigate, workspaceId, userRole, accessHelper, workspace?.id]);

  const activeSection = useMemo<SettingsSection>(() => {
    const path = location.pathname;
    const query = new URLSearchParams(location.search);
    const tab = (query.get("tab") || "").toLowerCase();

    if (path.includes("/settings/account")) {
      return tab === "security" ? "security" : "profile";
    }

    if (path.includes("/settings/security")) {
      return "security";
    }

    if (path.includes("/settings/workspace") || path.includes("/settings/business-context")) {
      return "general";
    }

    if (path.includes("/settings/teams")) {
      return "teams";
    }

    if (path.includes("/settings/members")) {
      return tab === "teams" ? "teams" : "members";
    }

    if (path.includes("/settings/api-keys")) {
      return "api-keys";
    }

    if (path.includes("/settings/segments")) {
      return "segments";
    }

    if (path.includes("/settings/alerts-assignment")) {
      return "alerts-assignment";
    }

    if (path.includes("/settings/meeting-automation")) {
      return "meeting-automation";
    }

    if (path.includes("/settings/email-settings")) {
      return "email-settings";
    }

    if (path.includes("/settings/domain-review")) {
      return "domain-review";
    }

    if (path.includes("/settings/ai-credits")) {
      return "ai-credits";
    }

    if (path.includes("/settings/tasks")) {
      return "tasks";
    }

    if (path.includes("/settings/health-score")) {
      return "health-score";
    }

    if (path.includes("/settings/computed-traits") || path.includes("/settings/data-management/metrics")) {
      return "computed-traits";
    }

    if (path.includes("/settings/data-management")) {
      return "objects";
    }

    if (path.includes("/settings/integrations/meeting-note-taker")) {
      return "meeting-notetaker";
    }

    if (path.includes("/settings/integrations/mcp")) {
      return "ai-connectors";
    }

    if (path.includes("/settings/integrations")) {
      return "connected-apps";
    }

    return "general";
  }, [location.pathname, location.search]);

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    if (!workspaceId) return [{ label: "Settings" }];

    const basePath = `/home/${workspaceId}/settings`;
    const path = location.pathname;
    const query = new URLSearchParams(location.search);
    const tab = (query.get("tab") || "").toLowerCase();

    if (path.includes("/settings/account") && tab === "security") {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Workspace", to: `${basePath}/workspace` },
        { label: "Security" },
      ];
    }

    if (path.includes("/settings/account")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "User", to: `${basePath}/account` },
        { label: "Profile" },
      ];
    }

    if (path.includes("/settings/security")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Workspace", to: `${basePath}/workspace` },
        { label: "Security" },
      ];
    }

    if (path.includes("/settings/workspace")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Workspace", to: `${basePath}/workspace` },
        { label: "General" },
      ];
    }

    if (path.includes("/settings/members")) {
      if (tab === "teams") {
        return [
          { label: "Settings", to: `${basePath}/workspace` },
          { label: "Team", to: `${basePath}/members` },
          { label: "Teams" },
        ];
      }
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Team", to: `${basePath}/members` },
        { label: "Members" },
      ];
    }

    if (path.includes("/settings/teams")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Team", to: `${basePath}/members` },
        { label: "Teams" },
      ];
    }

    if (path.includes("/settings/api-keys")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Developer", to: `${basePath}/api-keys` },
        { label: "API Keys" },
      ];
    }

    if (path.includes("/settings/segments")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/segments` },
        { label: "Segments" },
      ];
    }

    if (path.includes("/settings/alerts-assignment")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/alerts-assignment` },
        { label: "Alerts & Assignment" },
      ];
    }

    if (path.includes("/settings/meeting-automation")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/meeting-automation` },
        { label: "Meeting Automation" },
      ];
    }

    if (path.includes("/settings/tasks")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/tasks` },
        { label: "Tasks" },
      ];
    }

    if (path.includes("/settings/email-settings")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Email", to: `${basePath}/email-settings` },
        { label: "Email Settings" },
      ];
    }

    if (path.includes("/settings/domain-review")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Email", to: `${basePath}/email-settings` },
        { label: "Domain Review" },
      ];
    }

    if (path.includes("/settings/ai-credits")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Billing", to: `${basePath}/ai-credits` },
        { label: "AI Credits" },
      ];
    }

    if (path.includes("/settings/health-score/model/")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/health-score` },
        { label: "Health Scores", to: `${basePath}/health-score` },
        { label: "Model Builder" },
      ];
    }

    if (path.includes("/settings/health-score")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/health-score` },
        { label: "Health Scores" },
      ];
    }

    if (path.includes("/settings/computed-traits") || path.includes("/settings/data-management/metrics")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Data Model", to: `${basePath}/data-management/objects` },
        { label: "Computed Traits" },
      ];
    }

    if (path.includes("/settings/data-management")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Data Model", to: `${basePath}/data-management/objects` },
        { label: "Objects" },
      ];
    }

    if (path.includes("/settings/integrations/meeting-note-taker")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Preferences", to: `${basePath}/integrations/meeting-note-taker` },
        { label: "Meeting Note Taker" },
      ];
    }

    if (path.includes("/settings/integrations/mcp")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Integrations", to: `${basePath}/integrations` },
        { label: "AI Connectors (MCP)" },
      ];
    }

    if (path.includes("/settings/integrations/")) {
      const integrationSlug = path.split("/settings/integrations/")[1]?.split("/")[0] || "";
      const integrationLabel = INTEGRATION_LABELS[integrationSlug] || toTitleCase(integrationSlug);
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Integrations", to: `${basePath}/integrations` },
        { label: "Connected Apps", to: `${basePath}/integrations` },
        { label: integrationLabel || "Integration" },
      ];
    }

    if (path.includes("/settings/integrations")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Integrations", to: `${basePath}/integrations` },
        { label: "Connected Apps" },
      ];
    }

    if (path.includes("/settings/business-context")) {
      return [
        { label: "Settings", to: `${basePath}/workspace` },
        { label: "Workspace", to: `${basePath}/workspace` },
        { label: "General", to: `${basePath}/workspace` },
        { label: "Business Context" },
      ];
    }

    return [{ label: "Settings" }];
  }, [location.pathname, location.search, workspaceId]);

  if (!workspaceId) {
    return <Outlet />;
  }

  const basePath = `/home/${workspaceId}/settings`;
  const isHealthBuilderRoute = location.pathname.includes("/settings/health-score/model/");
  const isWideRoute = location.pathname.includes("/settings/alerts-assignment");

  const handleNavigateSection = (section: SettingsSection) => {
    switch (section) {
      case "profile":
        navigate(`${basePath}/account`);
        return;
      case "general":
        navigate(`${basePath}/workspace`);
        return;
      case "security":
        navigate(`${basePath}/security`);
        return;
      case "members":
        navigate(`${basePath}/members`);
        return;
      case "teams":
        navigate(`${basePath}/teams`);
        return;
      case "objects":
        navigate(`${basePath}/data-management/objects`);
        return;
      case "computed-traits":
        navigate(`${basePath}/computed-traits`);
        return;
      case "connected-apps":
        navigate(`${basePath}/integrations`);
        return;
      case "ai-connectors":
        navigate(`${basePath}/integrations/mcp`);
        return;
      case "api-keys":
        navigate(`${basePath}/api-keys`);
        return;
      case "segments":
        navigate(`${basePath}/segments`);
        return;
      case "health-score":
        navigate(`${basePath}/health-score`);
        return;
      case "alerts-assignment":
        navigate(`${basePath}/alerts-assignment`);
        return;
      case "tasks":
        navigate(`${basePath}/tasks`);
        return;
      case "meeting-automation":
        navigate(`${basePath}/meeting-automation`);
        return;
      case "email-settings":
        navigate(`${basePath}/email-settings`);
        return;
      case "domain-review":
        navigate(`${basePath}/domain-review`);
        return;
      case "ai-credits":
        navigate(`${basePath}/ai-credits`);
        return;
      case "exports":
        navigate(`/home/${workspaceId}/exports`);
        return;
      case "meeting-notetaker":
        navigate(`${basePath}/integrations/meeting-note-taker`);
        return;
      default:
        navigate(`${basePath}/workspace`);
    }
  };

  const handleBack = () => {
    navigate(`/home/${workspaceId}/headquarters`);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <SettingsSidebar
        activeSection={activeSection}
        onNavigate={handleNavigateSection}
        onBack={handleBack}
        userRole={userRole}
      />

      <main className={cn("flex-1", isHealthBuilderRoute ? "overflow-hidden" : "overflow-auto")}>
        <div
          className={cn(
            isHealthBuilderRoute
              ? "flex h-full flex-col px-6 py-6"
              : isWideRoute
                ? "mx-auto max-w-[1100px] px-8 py-8"
                : "mx-auto max-w-[720px] px-8 py-8",
          )}
        >
          <div className={cn("mb-6 flex items-center gap-1.5 text-[11px] text-muted-foreground", isHealthBuilderRoute && "mb-4 shrink-0")}>
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const key = `${item.label}-${index}`;
              return (
                <span key={key} className="flex items-center gap-1.5">
                  {index > 0 && <span>/</span>}
                  {isLast || !item.to ? (
                    <span className={isLast ? "text-foreground" : undefined}>{item.label}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(item.to as string)}
                      className="transition-colors hover:text-foreground"
                    >
                      {item.label}
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          <div className={cn(isHealthBuilderRoute && "min-h-0 flex-1")}>
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsLayout;
